import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { SectionLabel } from "./SectionLabel"

export interface SubTableColumn<T> {
  key: string
  header: string
  cell: (item: T) => ReactNode
  className?: string
  /**
   * Hide this column when the container is narrower than the given breakpoint.
   * Uses pre-defined container-query classes: "280px" | "360px" | "480px"
   */
  hideBelow?: "280px" | "360px" | "480px"
}

const HIDE_BELOW_CLASS: Record<string, string> = {
  "280px": "hidden @[280px]:table-cell",
  "360px": "hidden @[360px]:table-cell",
  "480px": "hidden @[480px]:table-cell",
}

interface EntitySubTableProps<T> {
  title: string
  items: T[]
  columns: SubTableColumn<T>[]
  keyOf: (item: T) => string
  hasMore: boolean
  onFetchMore: () => unknown
  isLoading?: boolean
  pageSize?: number
  total?: number
  onRowClick?: (item: T) => void
  /** Optional control rendered in the header (e.g. an "Add" button). When set,
   * the table is shown even with no items so the action stays reachable. */
  action?: ReactNode
  /** Override the outer wrapper padding (defaults to the detail-panel inset). */
  className?: string
  /** Extra class(es) applied to each body row (e.g. a distinct row background). */
  rowClassName?: string
  /** Show the total in the pagination footer ("Showing 1–5 · Total 40") instead
   * of the top-right of the header. */
  totalInFooter?: boolean
  /** Suppress the built-in title/count header row. Use when a parent already
   * renders that chrome, e.g. wrapping this table in `CollapsibleSection`. */
  hideHeader?: boolean
  /** Wrap the table in its own rounded, bordered card (default). Set to
   * `false` when a parent already provides that framing (e.g.
   * `CollapsibleSection`'s box) — stacking both nests a border inside a
   * border. */
  bordered?: boolean
  /** Suppress the `<thead>` column-header row, rendering plain data rows only.
   * Use alongside `bordered={false}` when nested in a section that already
   * labels the content (e.g. a single-column relation table inside
   * `CollapsibleSection`). */
  hideColumnHeader?: boolean
  /** Extra class(es) applied to the pagination footer row. Use to restore
   * left padding when `className` strips it for a flush, borderless table
   * (e.g. nested in `CollapsibleSection`). */
  footerClassName?: string
}

export function EntitySubTable<T>({
  title,
  items,
  columns,
  keyOf,
  hasMore,
  onFetchMore,
  isLoading,
  pageSize = 5,
  total,
  onRowClick,
  action,
  className,
  rowClassName,
  totalInFooter,
  hideHeader = false,
  bordered = true,
  hideColumnHeader = false,
  footerClassName,
}: EntitySubTableProps<T>) {
  const { translate } = useTranslation()
  const [page, setPage] = useState(0)
  const [isFetchingPage, setIsFetchingPage] = useState(false)
  const [isTransitioningPage, setIsTransitioningPage] = useState(false)
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  const start = page * pageSize
  const end = start + pageSize
  const visibleItems = items.slice(start, end)
  const totalFetched = items.length

  const canPrev = page > 0
  const canNext = end < totalFetched || hasMore
  const isLoadingPage =
    (isLoading ?? false) || isFetchingPage || isTransitioningPage

  // Page already sitting in memory (no API round-trip needed) — still show a
  // brief skeleton so pagination feels consistent whether or not a fetch is
  // actually happening underneath.
  function goToLocalPage(nextPage: number) {
    setIsTransitioningPage(true)
    transitionTimeoutRef.current = setTimeout(() => {
      setPage(nextPage)
      setIsTransitioningPage(false)
    }, 1000)
  }

  function handleNext() {
    const nextPage = page + 1
    if (end >= totalFetched && hasMore) {
      setIsFetchingPage(true)
      Promise.resolve(onFetchMore())
        .then((res) => {
          // The common caller is react-query's fetchNextPage(), which
          // resolves with the query result — including on failure — rather
          // than rejecting, so a .catch() here never actually runs. Check
          // `isError` on the resolved value instead of trusting rejection,
          // or a failed fetch silently advances onto a blank padded page.
          const failed =
            !!res &&
            typeof res === "object" &&
            (res as { isError?: boolean }).isError
          if (!failed) setPage(nextPage)
        })
        .catch(() => {
          // Defensive fallback for a caller that does reject — don't advance.
        })
        .finally(() => {
          setIsFetchingPage(false)
        })
    } else {
      goToLocalPage(nextPage)
    }
  }

  function handlePrev() {
    goToLocalPage(page - 1)
  }

  // Pad rows to pageSize so the table height never shifts between pages —
  // but only when pagination is actually in play. A single-page table sizes to content.
  const needsPagination = canPrev || canNext
  const skeletonRowCount = isLoadingPage
    ? pageSize
    : needsPagination
      ? Math.max(0, pageSize - visibleItems.length)
      : 0

  // Hide an empty section in read-only contexts, but keep it (with the action)
  // when the caller provides one so the "Add" control stays reachable.
  if (!isLoading && items.length === 0 && !action) {
    return null
  }

  const table = (
    <Table containerClassName="overflow-x-auto">
      {!hideColumnHeader && (
        <TableHeader>
          <TableRow className="border-grey4 bg-grey2 hover:bg-grey2">
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  "border-r-0 text-xs text-grey5",
                  col.hideBelow && HIDE_BELOW_CLASS[col.hideBelow],
                  col.className,
                )}
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      )}
      <TableBody>
        {/* Real rows — hidden while a page is loading */}
        {!isLoadingPage &&
          visibleItems.map((item) => (
            <TableRow
              key={keyOf(item)}
              className={cn(
                "border-grey4 hover:bg-white/3",
                onRowClick && "cursor-pointer",
                rowClassName,
              )}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(
                    "border-r-0 text-xs text-white",
                    col.hideBelow && HIDE_BELOW_CLASS[col.hideBelow],
                    col.className,
                  )}
                >
                  {col.cell(item)}
                </TableCell>
              ))}
            </TableRow>
          ))}

        {/* Skeleton rows — fill the table while loading, or pad short last pages */}
        {Array.from({ length: skeletonRowCount }).map((_, i) => (
          <TableRow
            // biome-ignore lint/suspicious/noArrayIndexKey: placeholders
            key={`sk-${i}`}
            className="border-grey4 hover:bg-transparent"
          >
            {columns.map((col) => (
              <TableCell
                key={col.key}
                className={cn(
                  "border-r-0",
                  col.hideBelow && HIDE_BELOW_CLASS[col.hideBelow],
                  col.className,
                )}
              >
                {isLoadingPage && <Skeleton className="h-3 w-3/4" />}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )

  const pagination = (canPrev ||
    canNext ||
    isLoadingPage ||
    (totalInFooter && !isLoading && items.length > 0)) && (
    <div
      className={cn(
        "my-1 flex items-center justify-between text-xs leading-none text-grey5",
        footerClassName,
      )}
    >
      <span>
        {isLoadingPage
          ? translate("MC_SUBTABLE_LOADING")
          : [
              translate("MC_SUBTABLE_SHOWING", {
                from: start + 1,
                to: Math.min(end, totalFetched),
              }),
              hasMore ? translate("MC_SUBTABLE_MORE") : null,
              totalInFooter && total != null
                ? translate("MC_SUBTABLE_TOTAL", { total })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-grey5 hover:text-white disabled:opacity-30"
          disabled={!canPrev || isLoadingPage}
          onClick={handlePrev}
        >
          <ChevronLeft size={14} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-grey5 hover:text-white disabled:opacity-30"
          disabled={!canNext || isLoadingPage}
          onClick={handleNext}
        >
          {isLoadingPage ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <ChevronRight size={14} />
          )}
        </Button>
      </div>
    </div>
  )

  return (
    <div className={cn("@container min-w-0 px-4 pb-4", className)}>
      {!hideHeader && (
        // flex-wrap: when the panel is narrowed, `action` (e.g. a "Link
        // article" button) drops to its own line below the title instead of
        // being pushed past the visible edge — same pattern as the "Open in
        // Supplier Studio" link in SupplierActiveWorkSection.tsx.
        <div className="flex flex-wrap items-center justify-between gap-2 py-3">
          <SectionLabel>{title}</SectionLabel>
          <div className="flex items-center gap-3">
            {!isLoading && total != null && !totalInFooter && (
              <span className="text-xs text-grey5">{total}</span>
            )}
            {action}
          </div>
        </div>
      )}

      {bordered ? (
        <div className="overflow-hidden rounded-lg border border-grey4">
          {table}
        </div>
      ) : (
        table
      )}

      {pagination}
    </div>
  )
}
